import _objectSpread from '@babel/runtime-corejs3/helpers/esm/objectSpread2';
import _objectWithoutProperties from '@babel/runtime-corejs3/helpers/esm/objectWithoutProperties';
import * as React from 'react';
import { jsx } from 'react/jsx-runtime';
import _Array$isArray from '@babel/runtime-corejs3/core-js-stable/array/is-array';
import _filterInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/filter';
import _mapInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/map';
import 'core-js/modules/web.dom-collections.iterator.js';
import { BrowserRouter, useRoutes, generatePath, matchRoutes } from 'react-router-dom';
import { StaticRouter } from 'react-router-dom/server';
import { formatUrl, joinUrlPaths } from '@thisisbud/url-utils';

/**
 * Get the name of a React component.
 *
 * @param Component - The React component to extract the name of
 * @returns The name of the component if available, or `Component` if unavailable
 * @public
 *
 * @example
 * ```javascript
 * import { getComponentName } from '@thisisbud/react-utils'
 *
 * function Foobar() {
 *   return <div>Foobar</div>
 * }
 *
 * const componentName = getComponentName(Foobar)
 * // 'Foobar'
 * ```
 */
function getComponentName(Component) {
  return Component.displayName || Component.name || 'Component';
}

const _excluded = ["children"];
function normalizeCtxName(ctxName) {
  return `${ctxName.substring(0, 1).toUpperCase()}${ctxName.substring(1)}`;
}
/**
 * Get a context provider React component.
 *
 * @param Ctx - The React context object
 * @param ctxName - The context name
 * @returns A React component that provides the value given by props to the context
 */
function getProvider(Ctx, ctxName) {
  function Provider(props) {
    const {
        children
      } = props,
      ctxProps = _objectWithoutProperties(props, _excluded);
    return /*#__PURE__*/jsx(Ctx.Provider, {
      value: ctxProps,
      children: children
    });
  }
  Provider.displayName = `${ctxName}Provider`;
  return Provider;
}
/**
 * Get a React hook to use the current value of the context.
 *
 * @param Ctx - The React context object
 * @returns A React hook to use the current value of the context
 */
function getUseContext(Ctx) {
  return function useContext() {
    return React.useContext(Ctx);
  };
}
/**
 * Get a High Order Component factory function to pass the current context value as props of a component.
 *
 * @param Ctx - The React context object
 * @param ctxName - The context name
 * @returns A High Order Component factory function
 */
function getWithContext(Ctx, ctxName) {
  return function withContext(Component) {
    function ComponentWithContextProps(props) {
      const ctxValue = React.useContext(Ctx);
      const combinedProps = _objectSpread(_objectSpread({}, props), ctxValue);
      return /*#__PURE__*/jsx(Component, _objectSpread({}, combinedProps)); // eslint-disable-line react/jsx-props-no-spreading
    }

    ComponentWithContextProps.WrappedComponent = Component;
    ComponentWithContextProps.displayName = `with${ctxName}(${getComponentName(Component)})`;
    return ComponentWithContextProps;
  };
}
/**
 * Create a new React context and related components, hooks and helpers.
 *
 * @param ctxName - The name of the context
 * @returns React components, hooks and helpers to use the created context
 * @public
 *
 * @example
 * ```javascript
 * import { createContext } from '@thisisbud/react-utils'
 *
 * export const { Provider, useContext, withContext } = createContext('config')
 * ```
 */
function createContext(ctxName = 'Context') {
  const Ctx = /*#__PURE__*/React.createContext(undefined); // eslint-disable-line @typescript-eslint/naming-convention
  const normalizedCtxName = normalizeCtxName(ctxName);
  return {
    Provider: getProvider(Ctx, normalizedCtxName),
    useContext: getUseContext(Ctx),
    withContext: getWithContext(Ctx, normalizedCtxName)
  };
}

function getRouterComponent(opts) {
  const {
    basePath = '/',
    location = '/'
  } = opts;
  const normalizedBasePath = basePath !== '/' && basePath !== '' ? basePath : undefined;
  return function Router(props) {
    const {
      children
    } = props;
    if (typeof window === 'undefined') {
      return /*#__PURE__*/jsx(StaticRouter, {
        basename: normalizedBasePath,
        location: location,
        children: children
      });
    }
    return /*#__PURE__*/jsx(BrowserRouter, {
      basename: normalizedBasePath,
      children: children
    });
  };
}
/**
 * Create a React component that renders the configured route elements.
 *
 * @param opts - The router options
 * @returns A React Routes component
 */
function getRoutesComponent(opts) {
  const {
    routes
  } = opts;
  return function Routes() {
    return useRoutes(routes);
  };
}
/**
 * Match a route name to one of the configured routes.
 *
 * @param routeName - The name of the route to match
 * @param routeObjects - The route haystack
 * @param stack - The matched route and its ancestors
 * @returns An array containing the matched route and its ancestors or `undefined` if not found
 */
function matchRouteByName(routeName, routeObjects, stack = []) {
  for (let i = 0; i < routeObjects.length; i++) {
    const route = routeObjects[i];
    if (route.name === routeName) {
      return [...stack, route];
    }
    if (_Array$isArray(route.children)) {
      const result = matchRouteByName(routeName, route.children, [...stack, route]);
      if (typeof result !== 'undefined') {
        return result;
      }
    }
  }
}
/**
 * Create a function to create HREFs for a given route name or a given path.
 *
 * @param opts - The router options
 * @returns A function to create HREFs for a route
 */
function getCreateHref(opts) {
  const {
    basePath = '/',
    routes
  } = opts;
  return function createHref(options) {
    var _context;
    const {
      hash,
      name,
      params,
      path,
      query
    } = options;
    if (typeof path === 'string') {
      return formatUrl({
        hash: hash,
        pathname: generatePath(joinUrlPaths(basePath, path), params),
        searchParams: query
      });
    }
    const routeMatch = matchRouteByName(name, routes);
    if (typeof routeMatch === 'undefined') {
      throw new Error(`A route with the name \`${name}\` was not found.`);
    }
    const routePaths = _filterInstanceProperty(_context = _mapInstanceProperty(routeMatch).call(routeMatch, function (route) {
      return route.path;
    })).call(_context, function (routePath) {
      return typeof routePath === 'string';
    });
    return formatUrl({
      hash: hash,
      pathname: generatePath(joinUrlPaths(basePath, ...routePaths), params),
      searchParams: query
    });
  };
}
/**
 * Get a function to run the route matching algorithm against a given location to get routes matches.
 *
 * @param opts - The router options
 * @returns A function to match routes to a location
 */
function getMatchRoutes(opts) {
  const {
    basePath = '/',
    routes
  } = opts;
  const normalizedBasePath = basePath !== '/' && basePath !== '' ? basePath : undefined;
  return function matchRoutes$1(location) {
    var _rrMatchRoutes;
    return (_rrMatchRoutes = matchRoutes(routes, location, normalizedBasePath)) !== null && _rrMatchRoutes !== void 0 ? _rrMatchRoutes : [];
  };
}
/**
 * Create components and utilities to set up a router in a React application using the `react-router-dom` library.
 *
 * @param options - The router options
 * @returns Components and utilities to set up a router in a React application
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { createRouter } from '@thisisbud/react-utils'
 * import HomeView from './views/home'
 *
 * const { Router, Routes } = createRouter({
 *   basePath: '/',
 *   routes: [
 *     {
 *       element: <HomeView/>,
 *       name: 'home',
 *       path: '',
 *     }
 *   ],
 * })
 *
 * export default function App() {
 *   return (
 *     <Router>
 *       <Routes/>
 *     </Router>
 *   )
 * }
 * ```
 */
function createRouter(options) {
  return {
    Router: getRouterComponent(options),
    Routes: getRoutesComponent(options),
    createHref: getCreateHref(options),
    matchRoutes: getMatchRoutes(options)
  };
}

/**
 * Unwrap a React component wrapped by one or more High Order Components.
 *
 * @remarks This is intended for testing purposes only
 * @param Component - A React component wrapped by High Order Components to unwrap
 * @returns The unwrapped component or the component itself if no wrapped components are found
 * @public
 *
 * @example
 * ```javascript
 * import { unwrapComponent } from '@thisisbud/react-utils'
 * import WrappedComponent from './wrapped-component'
 *
 * const Component = unwrapComponent(WrappedComponent)
 * ```
 */
function unwrapComponent(Component) {
  let output = Component;
  // @ts-expect-error The `WrappedComponent` property is a common practice for HOC's
  while (typeof output.WrappedComponent !== 'undefined') {
    // @ts-expect-error The `WrappedComponent` property is a common practice for HOC's
    output = output.WrappedComponent;
  }
  return output;
}

/**
 * Use a reference object to check if the component is mounted or not to avoid doing operations on unmounted components.
 *
 * @returns A React ref object with a boolean current value
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { useIsMounted } from '@thisisbud/react-utils'
 *
 * function useData() {
 *   const isMounted = useIsMounted()
 *   const [data, setData] = React.useState()
 *
 *   React.useEffect(function () {
 *     fetchData().then(function (fetchedData) {
 *       if (isMounted.current) {
 *         setData(fetchedData)
 *       }
 *     })
 *   }, [])
 *
 *   return data
 * }
 * ```
 */
function useIsMounted() {
  const state = React.useRef(true);
  React.useEffect(function () {
    return function () {
      state.current = false;
    };
  }, []);
  return state;
}

export { createContext, createRouter, getComponentName, unwrapComponent, useIsMounted };
